/* eslint-disable max-len */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { RootState } from '../../../../states/containers/store'
import {
  createTaskBulkLabeling,
  destroyTaskBulkLabeling,
  fetchTaskBulkLabeling,
  fetchTaskBulkLabelingNew,
  fetchTaskViewableLabelGroup,
  fetchTaskViewableLabelGroupDetail,
} from '../../../../states/containers/task/taskSlice'
import LoadingSquare from '../../../components/common/LoadingSquare'
import Tips from '../../../components/common/Tips'
import { IconName, IconPrefix } from '@fortawesome/fontawesome-common-types'
import { format, nowDate, prevMonth, secToHour, secToMinute } from '../../../../utils/date'
import { RequestGetTaskBulkLabeling, RequestPostTaskBulkLabeling } from '../../../../states/types/task'
import DateModule from '../../../components/task/MonthCalendarModule'
import BulkLabelingModal from '../../../components/task/BulkLabelingModal'
import { compile } from '../../../../utils/search'
import { sortBy, uniqueId } from 'lodash'
import Modal from '../../../components/common/Modal'

interface LabelGroup {
  id: number
  name: string
  color: string
  code: string | null
}

interface Member {
  id: number
  name: string
  profile_image: string | null
}

interface GroupedTask {
  gt_id: number
  ids: number[]
  type: 'ActiveTask' | 'ManualTask' | 'OnlineMeetingTask'
  service_name: string | null
  icon: string | null
  icon_color: string | null
  name: string | null
  file_path: string | null
  included_url_parts: string | null
  url: string | null
  time: number
  count: number
  labeling_status: 'single' | 'mixed' | 'none'
  label_group_id: number | null
  label_group_name: string | null
  label_group_color: string | null
  label_id: number | null
  label_name: string | null
  label_color: string | null
  contains_locked_task: boolean
}

interface Label {
  label_group_id: number | null
  label_group_name: string | null
  label_group_color: string | null
  label_id: number | null
  label_name: string | null
  label_color: string | null
}

const SEARCH_LIMIT = 1000

const Index: FC = () => {
  const {
    task,
    login,
  } = useSelector((state: RootState) => state)

  const RESERVED_LABEL_GROUP_ID = 2
  const DISPLAY_LABEL_NUMBER = 100
  const DISPLAY_BLOCK_NUMBER = 10

  const location = useLocation()
  const dispatch = useDispatch()

  const [taskDataList, setTaskDataList] = useState<GroupedTask[]>([])
  const [displayType, setDisplayType] = useState<string>("service_name") // service_name, label
  const [selectLabelGroup, setSelectLabelGroup] = useState<LabelGroup>()
  const [selectTargetDate, setSelectTargetDate] = useState<string>(prevMonth(nowDate(), "YYYY-MM-01"))
  const [isSearchConditionLoading, setIsSearchConditionLoading] = useState<boolean>(true)

  const [selectTaskIds, setSelectTaskIds] = useState<number[]>([])
  const [targetTaskIds, setTargetTaskIds] = useState<number[]>([])
  const [isSearchLoading, setIsSearchLoading] = useState<boolean>(false)
  const [isDisplayModal, setIsDisplayModal] = useState<boolean>(false)
  const [isSearch, setIsSearch] = useState<boolean>(false)

  const [selectMembers, setSelectMembers] = useState<Member[]>([])
  const [labelGroupList, setLabelGroupList] = useState<LabelGroup[]>([])
  const [labelGroupListInit, setLabelGroupListInit] = useState<LabelGroup[]>([])

  const [searchLabelGroupWord, setSearchLabelGroupWord] = useState<string>("")
  const [isWordLoading, setIsWordLoading] = useState<boolean>(false)
  const [composition, setComposition] = useState<boolean>(false)
  const [isSearched, setIsSearched] = useState<boolean>(false)

  const [isMemberLoading, setIsMemberLoading] = useState<boolean>(false)

  const searchLabelGroupInit = {
    id: 0,
    name: "",
    color: "",
    code: null,
  }
  const [searchLabelGroup, setSearchLabelGroup] = useState<LabelGroup>(searchLabelGroupInit)
  const [searchTargetDate, setSearchTargetDate] = useState<string>(nowDate("YYYY-MM-01"))

  const [isDisplaySelectLabelGroupModal, setIsDisplaySelectLabelGroupModal] = useState<boolean>(false)

  const [isSearchDetail, setIsSearchDetail] = useState<boolean>(true)

  const [searchApplications, setSearchApplications] = useState<string[]>([])
  const [searchWord, setSearchWord] = useState<string>("")
  const [formatTrigger, setFormatTrigger] = useState<string>("")
  const [searchedApplications, setSearchedApplications] = useState<string[]>([])
  const [searchedWord, setSearchedWord] = useState<string>("")

  const [displayNumList, setDisplayNumList] = useState<{id: number, name: string, displayNum: number}[]>([])
  const [displayBlockNum, setDisplayBlockNum] = useState<number>(1)

  const [isDisplayInfoModal, setIsDisplayInfoModal] = useState<boolean>(false)

  const [selectedLabel, setSelectedLabel] = useState<Label>()

  const [isFilterUnsortedOnly, setIsFilterUnsortedOnly] = useState<boolean>(false)

  const resetLabelGroup = () => {
    setSearchLabelGroupWord("")
    setLabelGroupList(labelGroupListInit)
  }

  const keyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && !composition) {
      event.currentTarget.blur()
    }
  }

  const searchLabelGroupList = (word?: string) => {
    let words: string[] = []
    if (word !== undefined) {
      words = word.split(/\s+/).filter(Boolean).map((v) => v.toLowerCase())
    } else {
      words = searchLabelGroupWord.split(/\s+/).filter(Boolean).map((v) => v.toLowerCase())
    }
    if (!words.length) {
      resetLabelGroup()
      return
    }
    if (login.permissions?.high_volume) {
      setIsWordLoading(true)
      dispatch(
        fetchTaskViewableLabelGroup(
          {
            keywords: words,
            limit: 1000,
          },
          (result) => {
            setLabelGroupList(result.label_groups)
            setIsWordLoading(false)
            setIsSearched(true)
          },
        ),
      )
      return
    }
    const list = labelGroupListInit.filter((labelGroup) => {
      if (labelGroup === null) return false
      return (compile(words).test(labelGroup.name.toLowerCase() + labelGroup.code?.toLocaleLowerCase()))
    })
    setLabelGroupList(sortBy(list, 'id'))
    setIsSearched(true)
  }

  const inputSearchWord = (word: string) => {
    setSearchLabelGroupWord(word)
    if (!login.permissions?.high_volume) searchLabelGroupList(word)
  }

  const search = (words: string) => {
    setSearchWord(words)
  }

  const groupedTasks: {task?: GroupedTask, label?: Label | null, list: GroupedTask[]}[] = useMemo(() => {
    let list = taskDataList.filter((v) => searchApplications.includes(v.service_name || ""))
    const words = searchWord.toLowerCase().split(/\s+/)
    if (words.length && list?.length) {
      list = list.filter((value) => {
        if (!value.name) return false
        return (compile(words).test(value.name.toLowerCase() + value.included_url_parts?.toLowerCase() + value.file_path?.toLowerCase() + value.url?.toLocaleLowerCase()))
      })
    }
    if (isFilterUnsortedOnly) {
      list = list.filter((v) => v.labeling_status === "none")
    }

    setSearchedApplications(searchApplications)
    setSearchedWord(searchWord)
    if (displayType === "service_name") {
      const serviceNameList = Array.from(new Set(list?.map((v) => v.service_name))).filter((v) => v !== null)
      setDisplayNumList(serviceNameList.map((v) => ({ id: 0, name: v || "", displayNum: 1 })) || [])
      const result = serviceNameList.map((v) => ({
        task: list?.find((w) => w.service_name === v),
        list: list?.filter((w) => w.service_name === v)
          .sort((a, b) => b.time - a.time) || [],
      }))
      if (list?.find((w) => !w.service_name)) {
        result.push(
          {
            task: list?.find((w) => !w.service_name),
            list: list?.filter((w) => !w.service_name)
              .sort((a, b) => b.time - a.time) || [],
          },
        )
      }
      return result
    }
    if (displayType === "label") {
      const labelList = Array.from(new Map(list?.map((v) => [v.label_id, v])).values()).map((v) => ({ label_id: v.label_id, label_name: v.label_name })).filter((v) => v.label_id !== null)
      setDisplayNumList(
        [
          ...labelList.map((v) => ({ id: v.label_id || 0, name: v.label_name || "", displayNum: 1 })),
          { id: 0, name: "未仕分け", displayNum: 1 },
          { id: 0, name: "複数の仕分け", displayNum: 1 },
        ],
      )
      const result = labelList.map((v) => {
        const targetLabel = list?.find((w) => w.label_id === v.label_id && w.labeling_status === "single")
        return ({
          label: {
            label_group_id: targetLabel?.label_group_id || null,
            label_group_name: targetLabel?.label_group_name || null,
            label_group_color: targetLabel?.label_group_color || null,
            label_id: targetLabel?.label_id || null,
            label_name: targetLabel?.label_name || null,
            label_color: targetLabel?.label_color || null,
          },
          list: list?.filter((w) => w.label_id === v.label_id && w.labeling_status === "single")
            .sort((a, b) => b.time - a.time) || [],
        })
      })
      if (list?.find((w) => w.labeling_status === "none")) {
        result.push(
          {
            label: {
              label_group_id: 0,
              label_group_name: "未仕分け",
              label_group_color: "",
              label_id: 0,
              label_name: "未仕分け",
              label_color: "",
            },
            list: list?.filter((w) => w.labeling_status === "none")
              .sort((a, b) => b.time - a.time) || [],
          },
        )
      }
      if (list?.find((w) => w.labeling_status === "mixed")) {
        result.push(
          {
            label: {
              label_group_id: 0,
              label_group_name: "複数の仕分け",
              label_group_color: "",
              label_id: 0,
              label_name: "複数の仕分け",
              label_color: "",
            },
            list: list?.filter((w) => w.labeling_status === "mixed")
              .sort((a, b) => b.time - a.time) || [],
          },
        )
      }
      return result.sort((a, b) => (a.label?.label_id || 0) - (b.label?.label_id || 0)) || []
    }
    return []
  }, [taskDataList, displayType, formatTrigger])

  const errorList = useMemo(() => {
    const tmpList = []
    if (!selectLabelGroup) {
      tmpList.push("プロジェクトを選択してください")
    }
    if (selectLabelGroup && !selectMembers.length) {
      tmpList.push("参加メンバーが存在するプロジェクトを選択してください")
    }
    return tmpList
  }, [selectLabelGroup, selectMembers])

  const targetList = useMemo(() => {
    if (!taskDataList) return []
    if (targetTaskIds.length) {
      return taskDataList.filter((v) => targetTaskIds.includes(v.gt_id))
    }
    return taskDataList.filter((v) => selectTaskIds.includes(v.gt_id))
  }, [selectTaskIds, targetTaskIds])

  const currentScroll = () => Math.max(window.pageYOffset, document.documentElement.scrollTop, document.body.scrollTop)
  const bottomHeight = () => document.getElementsByClassName("main")[0].clientHeight
  const windowHeight = () => document.documentElement.clientHeight

  const onScroll = () => {
    const adjustment = window.innerWidth >= 1280 ? 0.90 : 0.85
    if ((bottomHeight() * adjustment) < (currentScroll() + windowHeight())) {
      setDisplayBlockNum((self) => self + 1)
    }
  }

  const clickSubmit = (params: {labelGroupId: string, targetDate: string}) => {
    setIsSearch(true)
    setIsSearchLoading(true)
    const tmpReqValue: RequestGetTaskBulkLabeling = {
      label_group_id: params.labelGroupId,
      target_date: params.targetDate,
    }
    dispatch(
      fetchTaskBulkLabeling(
        tmpReqValue,
        (result) => {
          setIsSearchLoading(false)
          setSelectTaskIds([])
          setTargetTaskIds([])
          setSearchApplications(result.service_names || [])
          setSearchLabelGroup(selectLabelGroup || searchLabelGroupInit)
          setSearchTargetDate(params.targetDate)
          setDisplayBlockNum(1)
        },
      ),
    )
  }

  const clickCheckIds = (taskIds: number[], multi = false, all = false) => {
    if (!multi) {
      if (selectTaskIds.includes(taskIds[0])) {
        setSelectTaskIds(selectTaskIds.filter((v) => v !== taskIds[0]))
      } else {
        setSelectTaskIds([...selectTaskIds, taskIds[0]])
      }
    } else {
      const joinIds: number[] = []
      const ejectIds: number[] = []
      taskIds.forEach((taskId) => {
        if (selectTaskIds.includes(taskId)) {
          ejectIds.push(taskId)
        } else {
          joinIds.push(taskId)
        }
      })
      if (all) {
        setSelectTaskIds(Array.from(new Set([...selectTaskIds, ...joinIds])))
      } else {
        setSelectTaskIds(Array.from(new Set([...selectTaskIds.filter((v) => !ejectIds.includes(v)), ...joinIds])))
      }
    }
  }

  const reservedLabelClass = (labelGroupId: number) => {
    if (labelGroupId <= RESERVED_LABEL_GROUP_ID) {
      if (labelGroupId === 0) {
        return "not_belong"
      }
      if (labelGroupId === 1) {
        return "break"
      }
      if (labelGroupId === 2) {
        return "not_working"
      }
    }
    return ""
  }

  const buildLabelName = (labelId: number) => {
    if (labelId === 0) return "プロジェクトに属さない作業データ"
    if (labelId === 1) return "休憩"
    if (labelId === 2) return "業務外"
    return ""
  }

  const updateTaskLabel = (params: RequestPostTaskBulkLabeling) => {
    dispatch(
      createTaskBulkLabeling(
        {
          grouped_task_ids: params.grouped_task_ids,
          label_id: params.label_id,
        },
        () => {
          let tmpLabelParam: Label
          if (params.label_id > RESERVED_LABEL_GROUP_ID) {
            const targetLabelGroup = task.taskBulkLabeling?.label_groups.find((v) => v.id === selectLabelGroup?.id)
            const targetLabel = task.taskBulkLabeling?.labels.find((v) => v.id === params.label_id)
            if (!targetLabelGroup || !targetLabel) return
            tmpLabelParam = {
              label_group_id: targetLabelGroup.id,
              label_group_name: targetLabelGroup.name,
              label_group_color: targetLabelGroup.color,
              label_id: targetLabel.id,
              label_name: targetLabel.name,
              label_color: targetLabel.color,
            }
          } else {
            tmpLabelParam = {
              label_group_id: params.label_id,
              label_group_name: buildLabelName(params.label_id),
              label_group_color: null,
              label_id: params.label_id,
              label_name: buildLabelName(params.label_id),
              label_color: null,
            }
          }
          setSelectedLabel(tmpLabelParam)
          setTaskDataList((current) => current.map((v) => (targetTaskIds.includes(v.gt_id) ? { ...v, ...tmpLabelParam, labeling_status: "single" } : v)))
          setSelectTaskIds([])
          setFormatTrigger(uniqueId())
          setIsDisplayInfoModal(true)
        },
      ),
    )
    setIsDisplayModal(false)
  }

  const deleteTaskLabel = (groupedTaskIds: number[]) => {
    dispatch(
      destroyTaskBulkLabeling(
        {
          grouped_task_ids: groupedTaskIds,
        },
        () => {
          const tmpLabelParam = {
            label_group_id: null,
            label_group_name: "未仕分け",
            label_group_color: null,
            label_id: null,
            label_name: "未仕分け",
            label_color: null,
          }
          setSelectedLabel(tmpLabelParam)
          setTaskDataList((current) => current.map((v) => (targetTaskIds.includes(v.gt_id) ? { ...v, ...tmpLabelParam, labeling_status: "none" } : v)))
          setSelectTaskIds([])
          setFormatTrigger(uniqueId())
          setIsDisplayInfoModal(true)
        },
      ),
    )
    setIsDisplayModal(false)
  }

  const buildTime = (time: number) => (
    <>
      {
        time >= 60 ? (
          <>
            {!!secToHour(time) && `${secToHour(time)}時間`}
            {!!secToMinute(time) && `${secToMinute(time)}分`}
          </>
        ) : (
          <>
            1分未満
          </>
        )
      }
    </>
  )

  const clickApplication = (application: string) => {
    if (searchApplications.includes(application)) {
      setSearchApplications([...searchApplications.filter((v) => v !== application)])
    } else {
      setSearchApplications([...searchApplications, application])
    }
  }

  const buildLimitNumber = (data: {task?: GroupedTask, label?: Label | null, list: GroupedTask[]}) => {
    if (displayType === "service_name") {
      return DISPLAY_LABEL_NUMBER * (displayNumList.find((v) => v.name === data.task?.service_name)?.displayNum || 1)
    }
    if (displayType === "label") {
      if (!data.label?.label_id) {
        return DISPLAY_LABEL_NUMBER * (displayNumList.find((v) => v.name === data.label?.label_name)?.displayNum || 1)
      }
      return DISPLAY_LABEL_NUMBER * (displayNumList.find((v) => v.id === data.label?.label_id)?.displayNum || 1)
    }
    return DISPLAY_LABEL_NUMBER
  }

  const addLimitNumber = (data: {task?: GroupedTask, label?: Label | null, list: GroupedTask[]}) => {
    const scrollTop = window.scrollY
    setTimeout(() => {
      window.scroll(0, scrollTop)
    }, 50)
    if (displayType === "service_name") {
      setDisplayNumList((current) => current.map((v) => (v.name === data.task?.service_name ? { ...v, displayNum: v.displayNum + 1 } : v)))
      return
    }
    if (displayType === "label") {
      if (!data.label?.label_id) {
        setDisplayNumList((current) => current.map((v) => (v.name === data.label?.label_name ? { ...v, displayNum: v.displayNum + 1 } : v)))
        return
      }
      setDisplayNumList((current) => current.map((v) => (v.id === data.label?.label_id ? { ...v, displayNum: v.displayNum + 1 } : v)))
    }
  }

  const selectDisplayType = (type: string) => {
    setDisplayType(type)
    setDisplayBlockNum(1)
  }

  const closeInfoModal = () => {
    setIsDisplayInfoModal(false)
    document.querySelector('html')?.classList.remove('modal_open')
    setSelectedLabel(undefined)
  }

  const clickLabelGroup = (labelGroup: LabelGroup) => {
    setSelectLabelGroup(labelGroup)
    setIsDisplaySelectLabelGroupModal(false)
    setIsMemberLoading(true)
    inputSearchWord("")
    dispatch(
      fetchTaskViewableLabelGroupDetail(
        { id: labelGroup.id.toString() },
        (result) => {
          setSelectMembers(result.label_group?.usable_members || [])
          setIsMemberLoading(false)
        },
      ),
    )
  }

  const subTitle = (data: GroupedTask) => {
    let title = ""
    let label = ""
    if (data.file_path) {
      title = `ファイルパス: ${data.file_path}`
      label = `(${data.file_path})`
    } else if (data.url) {
      title = `URL: ${data.url}`
      label = `(${data.url})`
    } else if (data.included_url_parts) {
      title = `自動仕分け条件: ${data.included_url_parts}に該当`
      label = `自動仕分け条件: ${data.included_url_parts}に該当`
    }
    if (!title) return (<></>)
    return (
      <div
        style={{
          color: "#999999",
          fontSize: "11px",
        }}
        title={title}
      >
        {label}
      </div>
    )
  }

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location])

  useEffect(() => {
    if (login.permissions?.high_volume) {
      setIsSearchConditionLoading(false)
      return
    }
    dispatch(fetchTaskBulkLabelingNew(
      () => {
        setIsSearchConditionLoading(false)
      },
    ))
  }, [])

  useEffect(() => {
    document.addEventListener("scroll", onScroll)
    return () => {
      document.removeEventListener("scroll", onScroll)
    }
  }, [])

  useEffect(() => {
    setLabelGroupList(task.taskBulkLabeling?.label_groups || [])
    setLabelGroupListInit(task.taskBulkLabeling?.label_groups || [])
    setSearchApplications(task.taskBulkLabeling?.service_names || [])
    setTaskDataList(task.taskBulkLabeling?.grouped_tasks || [])
    setFormatTrigger(uniqueId())
  }, [task.taskBulkLabeling])

  useEffect(() => {
    if (!labelGroupListInit.length && !login.permissions?.high_volume) {
      return undefined
    }
    setIsSearched(false)
    const timer = setTimeout(() => {
      if (!composition) {
        searchLabelGroupList()
      }
    }, 700)
    return () => clearTimeout(timer)
  }, [searchLabelGroupWord, composition])

  return (
    <section className="search_page task_page">
      <Helmet>
        <title>Qasee - 工数管理 - 一括仕分け</title>
      </Helmet>
      <section className="breadcrumb">
        工数管理
        &nbsp;&gt;&nbsp;
        一括仕分け
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          一括仕分け
        </h1>
      </section>
      <section className="page_description">
        <p>
          工数管理で収集した作業データを選択して、業務の一括仕分けをすることができます。
        </p>
      </section>
      {
        isSearchConditionLoading ? (
          <>
            <LoadingSquare>
              <>
                一括仕分けを準備しています…
              </>
            </LoadingSquare>
          </>
        ) : (
          <>
            <section className="page_contents search_condition">
              <div className="content-block">
                <div className="block-header">
                  <div className="title" style={{ marginBottom: 0, color: "#000000" }}>
                    STEP.1 仕分けしたいプロジェクトから絞り込む
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexFlow: "column",
                    gap: "8px",
                    padding: "8px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      minHeight: "30px",
                    }}
                  >
                    <div
                      style={{
                        width: "100px",
                      }}
                    >
                      対象プロジェクト
                    </div>
                    <div
                      style={{
                        position: "relative",
                        width: "calc(100% - 100px)",
                      }}
                    >
                      <div
                        style={{
                          border: "solid 1px #CCCCCC",
                          borderRadius: "3px",
                          padding: "12px 8px",
                          fontSize: "12px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "4px",
                          width: "100%",
                          height: "40px",
                          position: "relative",
                          cursor: "pointer",
                          color: selectLabelGroup !== undefined ? "#333333" : "#CCCCCC",
                        }}
                        onClick={() => setIsDisplaySelectLabelGroupModal(true)}
                        role="none"
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          {
                            !!selectLabelGroup && (
                              <div
                                style={{
                                  backgroundColor: selectLabelGroup.color,
                                  width: "12px",
                                  height: "12px",
                                }}
                              />
                            )
                          }
                          {selectLabelGroup?.name || "プロジェクトを選択してください"}
                        </div>
                        <FontAwesomeIcon
                          icon={['fas', 'angle-down']}
                          style={{
                            fontSize: "20px",
                            color: "#666666",
                          }}
                        />
                      </div>
                      {
                        isDisplaySelectLabelGroupModal && (
                          <>
                            <div
                              style={{
                                position: "absolute",
                                top: "30px",
                                left: 0,
                                backgroundColor: "#FFFFFF",
                                border: "solid 1px #CCCCCC",
                                borderRadius: "4px",
                                width: "100%",
                                zIndex: "2004",
                                display: "flex",
                                flexFlow: "column",
                                gap: "4px",
                              }}
                            >
                              <div
                                style={{
                                  padding: "8px 16px",
                                  backgroundColor: "#EEEEEE",
                                  borderRadius: "4px 4px 0 0",
                                }}
                              >
                                仕分けしたいプロジェクトを選択
                              </div>
                              <div
                                style={{
                                  padding: "16px",
                                }}
                              >
                                <div
                                  className="search_area"
                                  style={{
                                    position: "relative",
                                    width: "100%",
                                    marginBottom: "8px",
                                    backgroundColor: "#FFFFFF",
                                    border: "solid 1px #CCCCCC",
                                    padding: "0 8px",
                                    borderRadius: "4px",
                                    fontSize: "12px",
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <FontAwesomeIcon
                                    icon={['fas', 'search']}
                                    className="icon"
                                  />
                                  <input
                                    type="text"
                                    placeholder="プロジェクトを検索"
                                    onChange={(event) => inputSearchWord(event.target.value)}
                                    onKeyDown={(event) => keyDown(event)}
                                    onCompositionStart={() => setComposition(true)}
                                    onCompositionEnd={() => setComposition(false)}
                                    value={searchLabelGroupWord}
                                    style={{
                                      width: "100%",
                                      fontSize: "12px",
                                      border: "none",
                                      backgroundColor: "transparent",
                                      padding: "8px 16px",
                                    }}
                                  />
                                  {
                                    searchLabelGroupWord && (
                                      <button type="button" className="reset" onClick={resetLabelGroup}>
                                        <FontAwesomeIcon icon={['far', 'times-circle']} />
                                      </button>
                                    )
                                  }
                                </div>
                                {
                                  isWordLoading ? (
                                    <LoadingSquare>
                                      <>
                                        プロジェクトデータを取得しています…
                                      </>
                                    </LoadingSquare>
                                  ) : (
                                    <>
                                      {
                                        !labelGroupList.length ? (
                                          <>
                                            <div
                                              style={{
                                                padding: "16px",
                                                backgroundColor: "#f5f7ff",
                                                textAlign: "center",
                                                fontSize: "16px",
                                                color: "#999999",
                                              }}
                                            >
                                              {
                                                searchLabelGroupWord.length ? (
                                                  <>
                                                    {
                                                      isSearched ? (
                                                        "キーワードに該当するプロジェクトがありませんでした"
                                                      ) : (
                                                        "検索中…"
                                                      )
                                                    }
                                                  </>
                                                ) : (
                                                  "キーワードを入力して、プロジェクトを検索してください"
                                                )
                                              }
                                            </div>
                                          </>
                                        ) : (
                                          <>
                                            {
                                              login.permissions?.high_volume && labelGroupList.length === SEARCH_LIMIT && (
                                                <div
                                                  style={{
                                                    display: "flex",
                                                    flexFlow: "column",
                                                    alignItems: "center",
                                                    gap: "4px",
                                                    border: "solid 1px #FF0000",
                                                    borderRadius: "4px",
                                                    backgroundColor: "#fff2f2",
                                                    color: "#FF0000",
                                                    padding: "8px",
                                                    marginBottom: "8px",
                                                  }}
                                                >
                                                  <div>
                                                    {`検索上位${SEARCH_LIMIT}件が表示されています`}
                                                  </div>
                                                  <div>
                                                    お探しのプロジェクトが見つからない時は、キーワードを詳細に入力してください
                                                  </div>
                                                </div>
                                              )
                                            }
                                            <div
                                              style={{
                                                display: "flex",
                                                gap: "4px",
                                                flexWrap: "wrap",
                                                fontSize: "10px",
                                                maxHeight: "400px",
                                                overflowY: "scroll",
                                                padding: "8px",
                                              }}
                                            >
                                              {
                                                labelGroupList.map((v) => (
                                                  <div
                                                    key={v.id}
                                                    onClick={() => clickLabelGroup(v)}
                                                    role="none"
                                                    style={{
                                                      backgroundColor: "#FFFFFF",
                                                      border: "solid 1px #CCCCCC",
                                                      borderRadius: "20px",
                                                      padding: "4px 8px",
                                                      display: "flex",
                                                      alignItems: "center",
                                                      gap: "4px",
                                                      cursor: "pointer",
                                                    }}
                                                  >
                                                    <div
                                                      style={{
                                                        backgroundColor: v.color,
                                                        width: "12px",
                                                        height: "12px",
                                                      }}
                                                    />
                                                    <div>
                                                      {v.name}
                                                      {v.code && `(${v.code})`}
                                                    </div>
                                                  </div>
                                                ))
                                              }
                                            </div>
                                          </>
                                        )
                                      }
                                    </>
                                  )
                                }
                              </div>
                            </div>
                            <div
                              className="close_screen gray"
                              role="presentation"
                              onClick={() => {
                                setIsDisplaySelectLabelGroupModal(false)
                                inputSearchWord("")
                              }}
                            />
                          </>
                        )
                      }
                    </div>
                  </div>
                  {
                    !!selectLabelGroup && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          minHeight: "30px",
                        }}
                      >
                        <div
                          style={{
                            width: "100px",
                          }}
                        />
                        <div
                          style={{
                            position: "relative",
                            width: "calc(100% - 100px)",
                          }}
                        >
                          <div
                            style={{
                              backgroundColor: "#FFFFFF",
                              border: "solid 1px #CCCCCC",
                              borderRadius: "4px",
                              width: "100%",
                            }}
                          >
                            <div
                              style={{
                                padding: "4px 8px",
                                backgroundColor: "#EEEEEE",
                                borderRadius: "4px 4px 0 0",
                                fontSize: "10px",
                              }}
                            >
                              検索対象メンバー
                            </div>
                            {
                              isMemberLoading ? (
                                <LoadingSquare>
                                  <>
                                    対象メンバーを検索しています…
                                  </>
                                </LoadingSquare>
                              ) : (
                                <>
                                  {
                                    selectMembers.length ? (
                                      <div
                                        style={{
                                          display: "flex",
                                          flexWrap: "wrap",
                                          gap: "4px",
                                          padding: "4px",
                                        }}
                                      >
                                        {
                                          selectMembers.map((v) => (
                                            <React.Fragment key={`select_member_${v.id}`}>
                                              <div
                                                style={{
                                                  backgroundColor: "#FFFFFF",
                                                  border: "solid 1px #CCCCCC",
                                                  borderRadius: "20px",
                                                  padding: "2px 8px",
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: "4px",
                                                  fontSize: "10px",
                                                }}
                                              >
                                                {
                                                  v.profile_image ? (
                                                    <img
                                                      alt="profile"
                                                      src={v.profile_image}
                                                      className="profile_icon"
                                                      style={{
                                                        borderRadius: "50%",
                                                        width: "14px",
                                                      }}
                                                    />
                                                  ) : (
                                                    <img
                                                      alt=""
                                                      src={`/static/images/profile/${(Number(v.id) || 0) % 12}.jpeg`}
                                                      className="profile_icon"
                                                      style={{
                                                        borderRadius: "50%",
                                                        width: "14px",
                                                      }}
                                                    />
                                                  )
                                                }
                                                {v.name}
                                              </div>
                                            </React.Fragment>
                                          ))
                                        }
                                      </div>
                                    ) : (
                                      <div
                                        style={{
                                          textAlign: "center",
                                          fontSize: "18px",
                                          fontWeight: "bold",
                                          color: "#999999",
                                          padding: "16px",
                                        }}
                                      >
                                        プロジェクト参加メンバーがいません
                                      </div>
                                    )
                                  }
                                </>
                              )
                            }
                          </div>
                        </div>
                      </div>
                    )
                  }
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      minHeight: "30px",
                    }}
                  >
                    <div
                      style={{
                        width: "100px",
                      }}
                    >
                      期間
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        position: "relative",
                      }}
                    >
                      <DateModule
                        date={selectTargetDate}
                        changeCallback={(date) => setSelectTargetDate(date)}
                        navigation={false}
                      />
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexFlow: "column",
                      gap: "4px",
                    }}
                  >
                    {
                      errorList.map((v) => (
                        <div
                          key={v}
                          style={{
                            border: "solid 1px #FF0000",
                            borderRadius: "4px",
                            color: "#FF0000",
                            backgroundColor: "#ffeded",
                            padding: "8px",
                          }}
                        >
                          {v}
                        </div>
                      ))
                    }
                  </div>
                  <div
                    style={{
                      textAlign: "center",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => !isSearchLoading && clickSubmit({
                        labelGroupId: selectLabelGroup?.id.toString() || "",
                        targetDate: selectTargetDate,
                      })}
                      style={{
                        border: `solid 1px ${!errorList.length ? "#507dc0" : "#999999"}`,
                        borderRadius: "4px",
                        backgroundColor: !errorList.length ? "#507dc0" : "#999999",
                        color: "#FFFFFF",
                        minWidth: "200px",
                        cursor: !errorList.length ? "pointer" : "not-allowed",
                      }}
                      disabled={!!errorList.length || isSearchLoading}
                    >
                      {
                        isSearchLoading ? (
                          <>
                            <FontAwesomeIcon icon={['fas', 'spinner']} spin />
                          </>
                        ) : (
                          <>
                            絞り込む
                          </>
                        )
                      }
                    </button>
                  </div>
                </div>
              </div>
            </section>
          </>
        )
      }
      {
        isSearchLoading ? (
          <>
            <LoadingSquare>
              <>
                一括仕分け対象を絞り込んでいます…
              </>
            </LoadingSquare>
          </>
        ) : (
          <>
            {
              isSearch && (
                <>
                  <section className="page_contents content-area">
                    <div className="content-block">
                      <div className="block-header">
                        <div className="title" style={{ marginBottom: 0 }}>
                          STEP.2 さらに絞り込む、または、一括仕分け対象を選択
                        </div>
                        <div
                          style={{
                            display: "flex",
                          }}
                        >
                          <div
                            onClick={() => selectDisplayType("service_name")}
                            role="none"
                            style={{
                              border: "solid 1px #999999",
                              borderRadius: "4px 0 0 4px",
                              padding: "4px 8px",
                              fontSize: "10px",
                              cursor: "pointer",
                              marginLeft: "-1px",
                              backgroundColor: displayType === "service_name" ? "#395F99" : "#FEFEFE",
                              color: displayType === "service_name" ? "#FFFFFF" : "#666666",
                            }}
                          >
                            アプリケーション別
                          </div>
                          <div
                            onClick={() => selectDisplayType("label")}
                            role="none"
                            style={{
                              border: "solid 1px #999999",
                              borderRadius: "0 4px 4px 0",
                              padding: "4px 8px",
                              fontSize: "10px",
                              cursor: "pointer",
                              marginLeft: "-1px",
                              backgroundColor: displayType === "label" ? "#395F99" : "#FEFEFE",
                              color: displayType === "label" ? "#FFFFFF" : "#666666",
                            }}
                          >
                            業務仕分け別
                          </div>
                        </div>
                      </div>
                      <div
                        style={{
                          border: "solid 1px #CCCCCC",
                          borderRadius: "4px",
                          backgroundColor: "#FFFFFF",
                          display: "flex",
                          flexFlow: "column",
                          alignItems: "baseline",
                          gap: "8px",
                          marginBottom: "8px",
                          fontSize: "12px",
                          color: "#666666",
                          width: "100%",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            width: "100%",
                            padding: "8px 12px",
                            cursor: "pointer",
                            borderBottom: isSearchDetail ? "solid 1px #CCCCCC" : "none",
                            backgroundColor: "#e9f1ff",
                            borderRadius: isSearchDetail ? "4px 4px 0 0" : "4px",
                          }}
                          onClick={() => setIsSearchDetail((current) => !current)}
                          role="none"
                        >
                          <div
                            style={{
                              display: "flex",
                              gap: "4px",
                            }}
                          >
                            <FontAwesomeIcon
                              icon={['fas', 'lightbulb']}
                              style={{
                                color: "#d8cb50",
                              }}
                            />
                            さらに絞り込む
                          </div>
                          <FontAwesomeIcon
                            icon={['fas', isSearchDetail ? 'angle-up' : 'angle-down']}
                            style={{
                              fontSize: "20px",
                              color: "#666666",
                            }}
                          />
                        </div>
                        {
                          isSearchDetail && (
                            <div
                              style={{
                                padding: "8px",
                                display: "flex",
                                flexFlow: "column",
                                gap: "16px",
                                width: "100%",
                              }}
                            >
                              <div
                                style={{
                                  width: "100%",
                                  position: "relative",
                                  border: "solid 1px #CCCCCC",
                                  borderRadius: "4px",
                                  padding: "12px",
                                }}
                              >
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "-8px",
                                    backgroundColor: "#FFFFFF",
                                    padding: "0 4px",
                                  }}
                                >
                                  アプリで絞り込む
                                </div>
                                {
                                  searchApplications.length !== task.taskBulkLabeling?.service_names.length ? (
                                    <button
                                      type="button"
                                      style={{
                                        border: "solid 1px #CCCCCC",
                                        borderRadius: "4px",
                                        backgroundColor: "#EEEEEE",
                                        fontSize: "10px",
                                        padding: "2px 4px",
                                        position: "absolute",
                                        top: "-8px",
                                        right: "12px",
                                      }}
                                      onClick={() => setSearchApplications(task.taskBulkLabeling?.service_names || [])}
                                    >
                                      すべて選択する
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      style={{
                                        border: "solid 1px #CCCCCC",
                                        borderRadius: "4px",
                                        backgroundColor: "#EEEEEE",
                                        fontSize: "10px",
                                        padding: "2px 4px",
                                        position: "absolute",
                                        top: "-8px",
                                        right: "12px",
                                      }}
                                      onClick={() => setSearchApplications([])}
                                    >
                                      すべて選択解除する
                                    </button>
                                  )
                                }
                                <div
                                  style={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: "4px",
                                  }}
                                >
                                  {
                                    task.taskBulkLabeling?.service_names.map((v) => (
                                      <div
                                        key={v}
                                        style={{
                                          backgroundColor: searchApplications.includes(v) ? "#f0fff0" : "#EEEEEE",
                                          border: "solid 1px #CCCCCC",
                                          borderRadius: "20px",
                                          padding: "2px 8px",
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "4px",
                                          fontSize: "10px",
                                          cursor: "pointer",
                                        }}
                                        onClick={() => clickApplication(v)}
                                        role="none"
                                      >
                                        <FontAwesomeIcon
                                          icon={["fas", searchApplications.includes(v) ? "check" : "ban"]}
                                          fixedWidth
                                          style={{
                                            color: searchApplications.includes(v) ? "#248a23" : "#666666",
                                          }}
                                        />
                                        {v}
                                      </div>
                                    ))
                                  }
                                </div>
                              </div>
                              <div
                                style={{
                                  width: "100%",
                                  position: "relative",
                                  border: "solid 1px #CCCCCC",
                                  borderRadius: "4px",
                                  padding: "8px",
                                }}
                              >
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "-8px",
                                    backgroundColor: "#FFFFFF",
                                    padding: "0 4px",
                                  }}
                                >
                                  キーワード・URL・ファイルパスで絞り込む
                                </div>
                                <div
                                  style={{
                                    position: "relative",
                                  }}
                                >
                                  <input
                                    type="text"
                                    className="no_border"
                                    style={{
                                      width: "100%",
                                    }}
                                    placeholder="キーワード・URL・ファイルパスを入力してください"
                                    onChange={(event) => search(event.target.value)}
                                    value={searchWord}
                                  />
                                  {
                                    !!searchWord.length && (
                                      <FontAwesomeIcon
                                        icon={['far', 'times-circle']}
                                        style={{
                                          color: "#666666",
                                          position: "absolute",
                                          top: "6px",
                                          right: "6px",
                                          fontSize: "16px",
                                        }}
                                        onClick={() => search("")}
                                      />
                                    )
                                  }
                                </div>
                              </div>
                              <div
                                onClick={() => setIsFilterUnsortedOnly((cur) => !cur)}
                                role="none"
                                style={{
                                  cursor: "pointer",
                                }}
                              >
                                <FontAwesomeIcon
                                  icon={["far", isFilterUnsortedOnly ? "check-square" : "square"]}
                                  fixedWidth
                                  style={{
                                    color: isFilterUnsortedOnly ? "#248a23" : "#666666",
                                  }}
                                />
                                未仕分けの作業のみ表示する
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "center",
                                }}
                              >
                                <button
                                  type="button"
                                  style={{
                                    border: "solid 1px #507dc0",
                                    borderRadius: "4px",
                                    backgroundColor: "#507dc0",
                                    color: "#FFFFFF",
                                    minWidth: "200px",
                                    cursor: "pointer",
                                    textAlign: "center",
                                  }}
                                  onClick={() => setFormatTrigger(uniqueId())}
                                >
                                  さらに絞り込む
                                </button>
                              </div>
                            </div>
                          )
                        }
                      </div>
                      <div
                        style={{
                          border: "solid 1px #CCCCCC",
                          borderRadius: "4px",
                          backgroundColor: "#FFFFFF",
                          padding: "8px 12px",
                          display: "flex",
                          flexFlow: "column",
                          alignItems: "baseline",
                          gap: "4px",
                          marginBottom: "8px",
                          fontSize: "10px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            padding: "4px",
                            borderRadius: "4px 4px 0 0",
                            fontSize: "14px",
                            marginBottom: "8px",
                          }}
                        >
                          絞り込み結果
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            flexFlow: "column",
                            gap: "4px",
                            width: "100%",
                          }}
                        >
                          <div
                            style={{
                              borderRadius: "4px",
                              backgroundColor: "#666666",
                              padding: "2px 4px",
                              color: "#FFFFFF",
                              fontSize: "8px",
                              whiteSpace: "nowrap",
                            }}
                          >
                            対象メンバー
                          </div>
                          <div
                            style={{
                              fontSize: "12px",
                              color: "#666666",
                              display: "flex",
                              flexWrap: "wrap",
                              alignItems: "center",
                              gap: "4px",
                              padding: "4px",
                            }}
                          >
                            {
                              selectMembers.map((v) => (
                                <React.Fragment key={`select_member_${v.id}`}>
                                  <div
                                    style={{
                                      backgroundColor: "#FFFFFF",
                                      border: "solid 1px #CCCCCC",
                                      borderRadius: "20px",
                                      padding: "2px 8px",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "4px",
                                      fontSize: "10px",
                                    }}
                                  >
                                    {
                                      v.profile_image ? (
                                        <img
                                          alt="profile"
                                          src={v.profile_image}
                                          className="profile_icon"
                                          style={{
                                            borderRadius: "50%",
                                            width: "14px",
                                          }}
                                        />
                                      ) : (
                                        <img
                                          alt=""
                                          src={`/static/images/profile/${(Number(v.id) || 0) % 12}.jpeg`}
                                          className="profile_icon"
                                          style={{
                                            borderRadius: "50%",
                                            width: "14px",
                                          }}
                                        />
                                      )
                                    }
                                    {v.name}
                                  </div>
                                </React.Fragment>
                              ))
                            }
                          </div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            flexFlow: "column",
                            gap: "4px",
                            width: "100%",
                          }}
                        >
                          <div
                            style={{
                              borderRadius: "4px",
                              backgroundColor: "#666666",
                              padding: "2px 4px",
                              color: "#FFFFFF",
                              fontSize: "8px",
                              whiteSpace: "nowrap",
                            }}
                          >
                            期間
                          </div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              fontSize: "12px",
                              color: "#666666",
                              padding: "4px",
                              width: "100%",
                            }}
                          >
                            <div>
                              {format(searchTargetDate, "YYYY年M月")}
                            </div>
                          </div>
                        </div>
                        {
                          searchedApplications.length !== task.taskBulkLabeling?.service_names.length && (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "flex-start",
                                flexFlow: "column",
                                gap: "4px",
                                width: "100%",
                              }}
                            >
                              <div
                                style={{
                                  borderRadius: "4px",
                                  backgroundColor: "#666666",
                                  padding: "2px 4px",
                                  color: "#FFFFFF",
                                  fontSize: "8px",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                アプリ
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  alignItems: "center",
                                  gap: "4px",
                                  fontSize: "12px",
                                  color: "#666666",
                                  padding: "4px",
                                  width: "100%",
                                }}
                              >
                                {
                                  searchedApplications.map((v) => (
                                    <div
                                      key={v}
                                      style={{
                                        backgroundColor: "#FFFFFF",
                                        border: "solid 1px #CCCCCC",
                                        borderRadius: "20px",
                                        padding: "2px 8px",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "4px",
                                        fontSize: "10px",
                                        cursor: "pointer",
                                      }}
                                    >
                                      {v}
                                    </div>
                                  ))
                                }
                              </div>
                            </div>
                          )
                        }
                        {
                          !!searchedWord.length && (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "flex-start",
                                flexFlow: "column",
                                gap: "4px",
                                width: "100%",
                              }}
                            >
                              <div
                                style={{
                                  borderRadius: "4px",
                                  backgroundColor: "#666666",
                                  padding: "2px 4px",
                                  color: "#FFFFFF",
                                  fontSize: "8px",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                キーワード・URL・ファイルパス
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  alignItems: "center",
                                  gap: "4px",
                                  fontSize: "12px",
                                  color: "#666666",
                                  padding: "4px",
                                  width: "100%",
                                }}
                              >
                                {searchedWord}
                              </div>
                            </div>
                          )
                        }
                      </div>
                      <div className="block-detail">
                        <div
                          style={{
                            display: "flex",
                            flexFlow: "column",
                            gap: "8px",
                          }}
                        >
                          {
                            !groupedTasks.length && (
                              <div
                                style={{
                                  textAlign: "center",
                                  fontSize: "20px",
                                  fontWeight: "bold",
                                  color: "#999999",
                                  margin: "16px",
                                }}
                              >
                                表示できるデータがありません
                              </div>
                            )
                          }
                          {
                            groupedTasks.slice(0, displayBlockNum * DISPLAY_BLOCK_NUMBER).map((groupedTask, i) => (
                              <React.Fragment key={`${displayType}_${i + 0}_${buildLimitNumber(groupedTask)}`}>
                                <div>
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      border: "solid 1px #CCCCCC",
                                      borderRadius: "4px 4px 0 0",
                                      padding: "4px 8px",
                                      marginBottom: "-1px",
                                      backgroundColor: "#EEEEEE",
                                      minHeight: "40px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        padding: "4px",
                                        display: "flex",
                                        alignItems: "baseline",
                                        gap: "4px",
                                        cursor: "pointer",
                                      }}
                                    >
                                      {
                                        displayType === "service_name" && (
                                          <>
                                            <div
                                              className="icon_area"
                                              style={{
                                                color: groupedTask.task?.icon_color || (groupedTask.task?.type !== "ActiveTask" && groupedTask.task?.name ? "#008800" : "#FF0000"),
                                              }}
                                            >
                                              {
                                                groupedTask.task?.icon ? (
                                                  <Tips
                                                    description={`${groupedTask.task?.service_name || ""}`}
                                                    className="normal"
                                                    icon={false}
                                                  >
                                                    <FontAwesomeIcon
                                                      icon={[groupedTask.task?.icon.split(" ")[0] as IconPrefix, groupedTask.task?.icon.split(" ")[1] as IconName]}
                                                      fixedWidth
                                                    />
                                                  </Tips>
                                                ) : (
                                                  <>
                                                    {
                                                      groupedTask.task?.type === "OnlineMeetingTask" ? (
                                                        <FontAwesomeIcon
                                                          icon={["fas", "video"]}
                                                          fixedWidth
                                                        />
                                                      ) : (
                                                        <FontAwesomeIcon
                                                          icon={groupedTask.task?.name ? ["fas", "check-circle"] : ["fas", "exclamation-triangle"]}
                                                          fixedWidth
                                                        />
                                                      )
                                                    }
                                                  </>
                                                )
                                              }
                                            </div>
                                            {
                                              groupedTask.task?.service_name ? (
                                                <>
                                                  {groupedTask.task.service_name}
                                                </>
                                              ) : (
                                                <>
                                                  {
                                                    groupedTask.task?.labeling_status === "mixed" ? (
                                                      <>
                                                        複数の仕分け
                                                      </>
                                                    ) : (
                                                      <>
                                                        その他
                                                      </>
                                                    )
                                                  }
                                                </>
                                              )
                                            }
                                          </>
                                        )
                                      }
                                      {
                                        displayType === "label" && (
                                          <>
                                            <div
                                              style={{
                                                width: "8px",
                                                height: "8px",
                                                borderRadius: "50%",
                                                backgroundColor: groupedTask.label?.label_group_color || "#CCCCCC",
                                              }}
                                            />
                                            {
                                              groupedTask.label?.label_group_id !== undefined ? (
                                                <>
                                                  {
                                                    (groupedTask.label?.label_group_id || 0) > RESERVED_LABEL_GROUP_ID ? (
                                                      <>
                                                        {`${groupedTask.label?.label_group_name}:${groupedTask.label?.label_name}`}
                                                      </>
                                                    ) : (
                                                      <>
                                                        {groupedTask.label?.label_group_name}
                                                      </>
                                                    )
                                                  }
                                                </>
                                              ) : (
                                                <>
                                                  {
                                                    groupedTask.task?.labeling_status === "mixed" ? (
                                                      <>
                                                        複数の仕分け
                                                      </>
                                                    ) : (
                                                      <>
                                                        未仕分け
                                                      </>
                                                    )
                                                  }
                                                </>
                                              )
                                            }
                                          </>
                                        )
                                      }
                                      {
                                        groupedTask.list.filter((v) => selectTaskIds.includes(v.gt_id)).length === groupedTask.list.length ? (
                                          <>
                                            <div
                                              style={{
                                                fontSize: "8px",
                                                color: "#3f51b5",
                                              }}
                                              onClick={() => clickCheckIds(groupedTask.list.map((v) => v.gt_id), true)}
                                              role="none"
                                            >
                                              選択解除する
                                            </div>
                                          </>
                                        ) : (
                                          <>
                                            <div
                                              style={{
                                                fontSize: "8px",
                                                color: "#3f51b5",
                                              }}
                                              onClick={() => clickCheckIds(groupedTask.list.map((v) => v.gt_id), true, true)}
                                              role="none"
                                            >
                                              すべて選択する
                                            </div>
                                          </>
                                        )
                                      }
                                    </div>
                                    {
                                      !!groupedTask.list.filter((v) => selectTaskIds.includes(v.gt_id)).length && (
                                        <div
                                          style={{
                                            display: "flex",
                                            alignItems: "baseline",
                                            gap: "2px",
                                            fontSize: "10px",
                                          }}
                                        >
                                          合計
                                          <div
                                            style={{
                                              fontSize: "18px",
                                              fontWeight: "bold",
                                              color: "#228b22",
                                            }}
                                          >
                                            {groupedTask.list.filter((v) => selectTaskIds.includes(v.gt_id)).length}
                                          </div>
                                          件 ( 合計時間
                                          <div
                                            style={{
                                              fontSize: "12px",
                                              fontWeight: "bold",
                                              color: "#228b22",
                                            }}
                                          >
                                            {buildTime(groupedTask.list.filter((v) => selectTaskIds.includes(v.gt_id)).reduce((s, v) => s + v.time, 0) || 0)}
                                          </div>
                                          ) の作業データが選択されています
                                          &nbsp;
                                          <button
                                            type="button"
                                            style={{
                                              border: "solid 1px #228b22",
                                              borderRadius: "4px",
                                              backgroundColor: "#228b22",
                                              color: "#FFFFFF",
                                              display: "flex",
                                              gap: "4px",
                                            }}
                                            disabled={!groupedTask.list.filter((v) => selectTaskIds.includes(v.gt_id)).length}
                                            onClick={() => {
                                              setTargetTaskIds(groupedTask.list.filter((v) => selectTaskIds.includes(v.gt_id)).map((v) => v.gt_id))
                                              setIsDisplayModal(true)
                                            }}
                                          >
                                            <FontAwesomeIcon
                                              icon={["fas", "check"]}
                                              fixedWidth
                                              style={{
                                                color: "#FFFFFF",
                                              }}
                                            />
                                            選択した作業データを一括仕分けする
                                          </button>
                                        </div>
                                      )
                                    }
                                  </div>
                                  <div
                                    style={{
                                      border: "solid 1px #CCCCCC",
                                      borderRadius: "0 0 4px 4px",
                                      padding: "8px",
                                      display: "flex",
                                      flexFlow: "column",
                                      gap: "4px",
                                    }}
                                  >
                                    {
                                      groupedTask.list.slice(0, buildLimitNumber(groupedTask)).map((v) => (
                                        <React.Fragment key={`${v.time}_${v.name}_${v.label_id}_${v.labeling_status}`}>
                                          <div
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: "8px",
                                            }}
                                          >
                                            <div
                                              title={`${v.service_name || ""} ${v.name || ""}`}
                                              style={{
                                                position: "relative",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                gap: "4px",
                                                cursor: "pointer",
                                                borderBottom: "solid 1px #EEEEEE",
                                                padding: "16px 8px 4px",
                                                minHeight: "40px",
                                                backgroundColor: "#FFFFFF",
                                                width: "calc(100% - 118px)",
                                              }}
                                              onClick={() => clickCheckIds([v.gt_id])}
                                              role="none"
                                            >
                                              <div
                                                style={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: "4px",
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    color: selectTaskIds.includes(v.gt_id) ? "#228b22" : "#666666",
                                                  }}
                                                >
                                                  <>
                                                    {
                                                      selectTaskIds.includes(v.gt_id) ? (
                                                        <>
                                                          <FontAwesomeIcon
                                                            icon={["fas", "check-square"]}
                                                            fixedWidth
                                                          />
                                                        </>
                                                      ) : (
                                                        <>
                                                          <FontAwesomeIcon
                                                            icon={["far", "square"]}
                                                            fixedWidth
                                                          />
                                                        </>
                                                      )
                                                    }
                                                  </>
                                                </div>
                                                <div
                                                  style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "2px",
                                                  }}
                                                >
                                                  <div
                                                    className="icon_area"
                                                    style={{
                                                      color: v.icon_color || (v.type !== "ActiveTask" && v.name ? "#008800" : "#FF0000"),
                                                    }}
                                                  >
                                                    {
                                                      v.icon ? (
                                                        <Tips
                                                          description={`${v.service_name || ""}`}
                                                          className="normal"
                                                          icon={false}
                                                        >
                                                          <FontAwesomeIcon
                                                            icon={[v.icon.split(" ")[0] as IconPrefix, v.icon.split(" ")[1] as IconName]}
                                                            fixedWidth
                                                          />
                                                        </Tips>
                                                      ) : (
                                                        <>
                                                          {
                                                            v.type === "OnlineMeetingTask" ? (
                                                              <FontAwesomeIcon
                                                                icon={["fas", "video"]}
                                                                fixedWidth
                                                              />
                                                            ) : (
                                                              <FontAwesomeIcon
                                                                icon={v.name ? ["fas", "check-circle"] : ["fas", "exclamation-triangle"]}
                                                                fixedWidth
                                                              />
                                                            )
                                                          }
                                                        </>
                                                      )
                                                    }
                                                  </div>
                                                  <div
                                                    className="text_area"
                                                  >
                                                    {v.name || (v.type !== "ActiveTask" ? "設定されていません" : "")}
                                                    {subTitle(v)}
                                                  </div>
                                                </div>
                                              </div>
                                              <div
                                                style={{
                                                  display: "flex",
                                                  alignItems: "baseline",
                                                  gap: "4px",
                                                }}
                                              >
                                                <>
                                                  {
                                                    v.label_group_id === null && (
                                                      <div className="label_wrapper no_label">
                                                        <div
                                                          className="label"
                                                          style={{
                                                            backgroundColor: v.labeling_status === "mixed" ? "#ffc20b" : "#FFFFFF",
                                                            border: `solid 1px ${v.labeling_status === "mixed" ? "#000000" : "#e44242"}`,
                                                            borderRadius: "4px",
                                                            color: v.labeling_status === "mixed" ? "#000000" : "#e44242",
                                                            fontSize: "10px",
                                                            padding: "2px 4px",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: "4px",
                                                            pointerEvents: "initial",
                                                            cursor: "default",
                                                            whiteSpace: "nowrap",
                                                          }}
                                                        >
                                                          { v.labeling_status === "mixed" ? "複数の仕分け" : "未仕分け"}
                                                        </div>
                                                      </div>
                                                    )
                                                  }
                                                  {
                                                    v.label_group_id !== null
                                                    && v.label_group_id > RESERVED_LABEL_GROUP_ID
                                                    && (
                                                      <div className="label_wrapper">
                                                        <div
                                                          className="label"
                                                          style={{
                                                            backgroundColor: "#FFFFFF",
                                                            border: "solid 1px #747474",
                                                            borderRadius: "4px",
                                                            fontSize: "10px",
                                                            padding: "2px 4px",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: "4px",
                                                            pointerEvents: "initial",
                                                            cursor: "default",
                                                            whiteSpace: "nowrap",
                                                          }}
                                                        >
                                                          <div
                                                            style={{
                                                              width: "8px",
                                                              height: "8px",
                                                              backgroundColor: v.label_group_color || "#CCCCCC",
                                                            }}
                                                          />
                                                          {`${v.label_group_name}:${v.label_name || ""}`}
                                                        </div>
                                                      </div>
                                                    )
                                                  }
                                                  {
                                                    v.label_group_id !== null && v.label_group_id <= RESERVED_LABEL_GROUP_ID && (
                                                      <div className={`label_wrapper ${reservedLabelClass(v.label_group_id)}`}>
                                                        <div
                                                          className="label"
                                                          style={{
                                                            backgroundColor: "#FFFFFF",
                                                            border: "solid 1px #747474",
                                                            borderRadius: "4px",
                                                            fontSize: "10px",
                                                            padding: "2px 4px",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: "4px",
                                                            pointerEvents: "initial",
                                                            cursor: "default",
                                                            whiteSpace: "nowrap",
                                                          }}
                                                        >
                                                          {`${v.label_group_name}`}
                                                        </div>
                                                      </div>
                                                    )
                                                  }
                                                </>
                                                <div
                                                  className="time_area"
                                                  style={{
                                                    display: "flex",
                                                    alignItems: "baseline",
                                                    justifyContent: "end",
                                                    gap: "2px",
                                                    color: "#999999",
                                                    fontSize: "10px",
                                                    minWidth: "140px",
                                                  }}
                                                >
                                                  合計時間
                                                  <div
                                                    style={{
                                                      fontSize: "16px",
                                                    }}
                                                  >
                                                    {buildTime(v.time)}
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                            <div>
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  setTargetTaskIds([v.gt_id])
                                                  setIsDisplayModal(true)
                                                }}
                                                style={{
                                                  border: "1px solid #228b22",
                                                  borderRadius: "4px",
                                                  backgroundColor: "#228b22",
                                                  color: "#ffffff",
                                                  display: "flex",
                                                  gap: "4px",
                                                  fontSize: "10px",
                                                }}
                                              >
                                                一括仕分けする
                                              </button>
                                            </div>
                                          </div>
                                        </React.Fragment>
                                      ))
                                    }
                                    {
                                      groupedTask.list.length > buildLimitNumber(groupedTask) && (
                                        <div
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                          }}
                                        >
                                          <button
                                            type="button"
                                            style={{
                                              borderRadius: "20px",
                                              padding: "8px 40px",
                                              backgroundColor: "#0069c4",
                                              color: "#FFFFFF",
                                            }}
                                            onClick={() => addLimitNumber(groupedTask)}
                                          >
                                            続きをみる
                                          </button>
                                        </div>
                                      )
                                    }
                                  </div>
                                </div>
                              </React.Fragment>
                            ))
                          }
                        </div>
                      </div>
                    </div>
                  </section>
                </>
              )
            }
          </>
        )
      }
      {
        isDisplayInfoModal && (
          <Modal
            show={isDisplayInfoModal}
            wrapperClass="w900"
            closeCallback={closeInfoModal}
            closeable
          >
            <>
              <div
                style={{
                  textAlign: "center",
                  fontSize: "20px",
                  fontWeight: "bold",
                  marginBottom: "24px",
                  color: "#008800",
                }}
              >
                一括仕分けが完了しました
              </div>
              <div
                style={{
                  display: "flex",
                  flexFlow: "column",
                  gap: "8px",
                  fontSize: "12px",
                }}
              >
                <div>
                  一括仕分けが完了しました。
                </div>
                {
                  (selectedLabel?.label_group_id || 0) > RESERVED_LABEL_GROUP_ID && (
                    <>
                      <div
                        style={{
                          display: "flex",
                          flexFlow: "column",
                          gap: "4px",
                          border: "solid 1px #CCCCCC",
                          borderRadius: "4px",
                          padding: "8px",
                          backgroundColor: "#edffed",
                        }}
                      >
                        <div>
                          変更した作業データは、Qaseeが自動仕分けデータとして学習します。
                        </div>
                        <div
                          style={{
                            display: "flex",
                            gap: "2px",
                            alignItems: "center",
                          }}
                        >
                          <div>
                            今後収集される作業データは自動的に
                          </div>
                          <div>
                            <div
                              className="label"
                              style={{
                                backgroundColor: "#FFFFFF",
                                border: "solid 1px #747474",
                                borderRadius: "4px",
                                fontSize: "10px",
                                padding: "2px 4px",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                pointerEvents: "initial",
                                cursor: "default",
                                whiteSpace: "nowrap",
                              }}
                            >
                              <div
                                style={{
                                  width: "8px",
                                  height: "8px",
                                  backgroundColor: selectedLabel?.label_group_color || "#CCCCCC",
                                }}
                              />
                              {`${selectedLabel?.label_group_name}:${selectedLabel?.label_name}`}
                            </div>
                          </div>
                          <div>
                            に設定されるようになります。
                          </div>
                        </div>
                      </div>
                    </>
                  )
                }
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    marginTop: "32px",
                  }}
                >
                  <button
                    type="button"
                    style={{
                      border: `solid 1px #507DC0`,
                      borderRadius: "4px",
                      backgroundColor: "#507DC0",
                      color: "#FFFFFF",
                      minWidth: "130px",
                      textAlign: "center",
                    }}
                    onClick={closeInfoModal}
                  >
                    閉じる
                  </button>
                </div>
              </div>
            </>
          </Modal>
        )
      }
      <BulkLabelingModal
        targetList={targetList}
        labelGroup={searchLabelGroup}
        labelList={task.taskBulkLabeling?.labels || []}
        closeCallback={() => {
          setIsDisplayModal(false)
          setTargetTaskIds([])
        }}
        updateCallback={(params) => updateTaskLabel(params)}
        deleteCallback={(taskIds) => deleteTaskLabel(taskIds)}
        isDisplay={isDisplayModal}
      />
    </section>
  )
}

export default Index
